import { http } from './request'
import {
  OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS,
  parseImportChunkResult,
  parseImportPreviewResult,
} from '@/types/importPreview'

/** 与后台 status：0 暂停 1 正常 2 限制 对应 */
export type AgencyStatus = 'normal' | 'paused' | 'restricted'

function num(v: unknown): number {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/**
 * 机构类型展示：与后台 orgType 枚举一致（驻外使馆/驻外总领馆/合作机构/贵州省外事办）
 */
export function normalizeAgencyOrgTypeTag(tag: string | undefined | null): string {
  const t = String(tag ?? '').trim()
  if (t === '驻外使馆' || t === '驻外总领馆' || t === '合作机构' || t === '贵州省外事办') return t
  if (t === '驻外领事馆') return '驻外使馆'
  return t || '驻外总领馆'
}

/** GET /biz/overseas/institution/statistics — 按机构类型分组 */
export interface OrgTypeBreakdownItem {
  orgType: string
  totalCount: number
  monthNewCount: number
}

/** GET /biz/overseas/institution/statistics — 解析后的统计 */
export interface OverseasInstitutionStatistics {
  totalInstitutionCount: number
  monthNewInstitutionCount: number
  embassyConsulateCount: number
  embassyCountryCoverageCount: number
  partnerInstitutionCount: number
  monthNewPartnerCount: number
  monthContactCount: number
  orgTypeBreakdown: OrgTypeBreakdownItem[]
}

function mapOrgTypeBreakdown(raw: unknown): OrgTypeBreakdownItem[] {
  if (!Array.isArray(raw)) return []
  const out: OrgTypeBreakdownItem[] = []
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    out.push({
      orgType: pickStr(o, 'orgType'),
      totalCount: num(o.totalCount),
      monthNewCount: num(o.monthNewCount),
    })
  }
  return out
}

/**
 * GET /biz/overseas/institution/statistics — 驻外机构列表页顶部统计
 */
export async function fetchOverseasInstitutionStatistics(): Promise<OverseasInstitutionStatistics> {
  const raw = await http.get<Record<string, unknown>>('/biz/overseas/institution/statistics')
  const o = raw != null && typeof raw === 'object' ? raw : {}
  return {
    totalInstitutionCount: num(o.totalInstitutionCount),
    monthNewInstitutionCount: num(o.monthNewInstitutionCount),
    embassyConsulateCount: num(o.embassyConsulateCount),
    embassyCountryCoverageCount: num(o.embassyCountryCoverageCount),
    partnerInstitutionCount: num(o.partnerInstitutionCount),
    monthNewPartnerCount: num(o.monthNewPartnerCount),
    monthContactCount: num(o.monthContactCount),
    orgTypeBreakdown: mapOrgTypeBreakdown(o.orgTypeBreakdown),
  }
}

export interface OverseasAgencyItem {
  id: string
  name: string
  /** 国家名称（展示） */
  country: string
  /** 国家二位码（便于筛选/回填） */
  countryCode?: string
  /** GeoNames 国家 geonameid（便于后端/地图） */
  geonameid?: number
  /** 大洲 */
  continent?: string
  city: string
  tag: string
  /** 机构 Logo（通用上传返回的文件 ID 或完整 URL，选填） */
  logoUrl?: string
  address: string
  phone: string
  email: string
  website: string
  serviceCount: number
  workTime: string
  hotline: string
  services: string[]
  status: AgencyStatus
  /** 创建时间（用于“无更新时间则显示创建时间”） */
  createdAt: string
  updatedAt: string
  /** 简介（列表/详情接口 introduction 映射，编辑表单回写） */
  summary?: string
}

/** GET /biz/overseas/institution/page 查询参数 */
export interface OverseasAgencyPageParams {
  page: number
  pageSize: number
  /** 机构名称/地址 */
  searchKey?: string
  /** 国家名称（模糊） */
  countryName?: string
  /** 国家二位码（精确，可选） */
  countryCode?: string
  /** 机构类型 */
  orgType?: string
  status?: AgencyStatus
}

/** 分页原始体（Spring 风格） */
interface OverseasInstitutionPageData {
  records?: unknown[]
  list?: unknown[]
  rows?: unknown[]
  total?: number
  totalCount?: number
  totalElements?: number
  size?: number
  current?: number
}

function totalFromInstitutionPage(raw: OverseasInstitutionPageData): number {
  const t = raw.total ?? raw.totalCount ?? raw.totalElements
  return num(t)
}

function recordsFromInstitutionPage(raw: OverseasInstitutionPageData): unknown[] {
  const a = raw.records ?? raw.list ?? raw.rows
  return Array.isArray(a) ? a : []
}

function agencyStatusFromApi(st: unknown): AgencyStatus {
  if (st == null || st === '') return 'normal'
  const n = Number(st)
  if (!Number.isFinite(n)) return 'normal'
  if (n === 0) return 'paused'
  if (n === 2) return 'restricted'
  return 'normal'
}

/** 列表查询：前端枚举 → 后台 0/1/2 */
function agencyStatusToQueryParam(status?: AgencyStatus): number | undefined {
  if (status === undefined) return undefined
  if (status === 'paused') return 0
  if (status === 'restricted') return 2
  return 1
}

function mapInstitutionRecordToItem(raw: unknown): OverseasAgencyItem | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickStr(o, 'id')
  if (!id) return null
  const mainServices = pickStr(o, 'mainServices')
  const services = mainServices
    ? mainServices
        .split(/[,，、]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []
  return {
    id,
    name: pickStr(o, 'name'),
    country: pickStr(o, 'countryName', 'country'),
    countryCode: pickStr(o, 'countryCode') || undefined,
    geonameid:
      typeof o.geonameid === 'number'
        ? (o.geonameid as number)
        : Number.isFinite(Number(o.geonameid))
          ? Number(o.geonameid)
          : undefined,
    continent: pickStr(o, 'continent') || undefined,
    city: pickStr(o, 'city'),
    tag: normalizeAgencyOrgTypeTag(pickStr(o, 'orgType', 'tag')),
    logoUrl: pickStr(o, 'logoUrl') || undefined,
    address: pickStr(o, 'address'),
    phone: pickStr(o, 'phone'),
    email: pickStr(o, 'email'),
    website: pickStr(o, 'website'),
    serviceCount: num(o.serviceStaffCount),
    workTime: pickStr(o, 'workingHours', 'workTime'),
    hotline: pickStr(o, 'emergencyPhone', 'hotline'),
    services,
    status: agencyStatusFromApi(o.status),
    createdAt: pickStr(o, 'createTime', 'create_time'),
    updatedAt: pickStr(o, 'updateTime', 'updatedAt', 'updatedTime'),
    summary: pickStr(o, 'introduction', 'summary'),
  }
}

function coordToFormString(v: unknown): string {
  if (v == null || v === '') return ''
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return String(v).trim()
}

/** GET /biz/overseas/institution/detail — 出参与新增/编辑体字段一致 */
function mapInstitutionRecordToDetail(raw: unknown): OverseasAgencyDetail | null {
  const item = mapInstitutionRecordToItem(raw)
  if (!item) return null
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  return {
    ...item,
    contactPerson: pickStr(o, 'contactPerson'),
    longitude: coordToFormString(o.locationLng ?? o.longitude),
    latitude: coordToFormString(o.locationLat ?? o.latitude),
    summary: pickStr(o, 'introduction', 'summary'),
  }
}

export interface OverseasAgencyPageResult {
  list: OverseasAgencyItem[]
  total: number
}

export interface OverseasAgencyDetail extends OverseasAgencyItem {
  contactPerson: string
  longitude: string
  latitude: string
  summary: string
}

export interface UpdateOverseasAgencyPayload {
  name?: string
  countryCode?: string
  countryName?: string
  continent?: string
  geonameid?: number | null
  city?: string
  tag?: string
  logoUrl?: string
  serviceCount?: number
  status?: AgencyStatus
  services?: string[]
  address: string
  phone: string
  email: string
  website: string
  workTime: string
  hotline: string
  summary: string
  /** 编辑提交时与新增对齐，供接口映射 */
  contactPerson?: string
  longitude?: string
  latitude?: string
}

export interface CreateOverseasAgencyPayload {
  name: string
  countryCode: string
  countryName: string
  continent?: string
  geonameid?: number | null
  city: string
  tag: string
  logoUrl?: string
  address: string
  phone: string
  email: string
  website: string
  serviceCount: number
  workTime: string
  hotline: string
  status: AgencyStatus
  services: string[]
  contactPerson: string
  longitude: string
  latitude: string
  summary: string
}

/** 后端 status：0 暂停 1 正常 2 限制 */
type InstitutionApiStatus = 0 | 1 | 2

function agencyStatusToApi(status: AgencyStatus): InstitutionApiStatus {
  if (status === 'paused') return 0
  if (status === 'restricted') return 2
  return 1
}

function parseCoord(v: string | undefined): number | null {
  if (v == null || String(v).trim() === '') return null
  const n = Number(String(v).trim())
  return Number.isFinite(n) ? n : null
}

function resolveOrgType(tag: string): string {
  return normalizeAgencyOrgTypeTag(tag)
}

type AgencyFormLike = {
  name: string
  countryCode: string
  countryName: string
  continent?: string
  geonameid?: number | null
  city: string
  tag: string
  logoUrl?: string
  address: string
  phone: string
  email: string
  website: string
  serviceCount: number
  workTime: string
  hotline: string
  status: AgencyStatus
  services: string[]
  contactPerson: string
  longitude: string
  latitude: string
  summary: string
}

function toAgencyFormLike(
  p:
    | CreateOverseasAgencyPayload
    | (UpdateOverseasAgencyPayload & {
        contactPerson?: string
        longitude?: string
        latitude?: string
      }),
): AgencyFormLike {
  return {
    name: p.name ?? '',
    countryCode: p.countryCode ?? '',
    countryName: p.countryName ?? '',
    continent: p.continent,
    geonameid: p.geonameid ?? null,
    city: p.city ?? '',
    tag: p.tag ?? '驻外总领馆',
    logoUrl: p.logoUrl,
    address: p.address,
    phone: p.phone,
    email: p.email,
    website: p.website,
    serviceCount: typeof p.serviceCount === 'number' ? p.serviceCount : 0,
    workTime: p.workTime,
    hotline: p.hotline,
    status: p.status ?? 'normal',
    services: Array.isArray(p.services) ? p.services : [],
    contactPerson: p.contactPerson ?? '',
    longitude: p.longitude ?? '',
    latitude: p.latitude ?? '',
    summary: p.summary ?? '',
  }
}

/**
 * POST /biz/overseas/institution/add、/edit 请求体（字段名与后台一致）
 * 图片未覆盖的字段：大洲/国家代码/geonameid/Logo 等由表单提交；负责人、备注页面未采集，编辑时由后端保留原值。
 */
function buildOverseasInstitutionSaveBody(
  p:
    | CreateOverseasAgencyPayload
    | (UpdateOverseasAgencyPayload & {
        contactPerson?: string
        longitude?: string
        latitude?: string
      }),
  id?: string,
): Record<string, unknown> {
  const f = toAgencyFormLike(p)
  const body: Record<string, unknown> = {
    name: f.name,
    orgType: resolveOrgType(f.tag),
    geonameid: f.geonameid ?? null,
    continent: (f.continent ?? '').trim(),
    countryCode: f.countryCode.trim(),
    countryName: f.countryName.trim(),
    city: f.city,
    address: f.address,
    locationLat: parseCoord(f.latitude),
    locationLng: parseCoord(f.longitude),
    contactPerson: f.contactPerson,
    phone: f.phone,
    emergencyPhone: f.hotline,
    email: f.email,
    website: f.website,
    serviceStaffCount: f.serviceCount,
    logoUrl: (f.logoUrl ?? '').trim(),
    workingHours: f.workTime,
    mainServices: f.services.length ? f.services.join('、') : '',
    introduction: f.summary,
    status: agencyStatusToApi(f.status),
  }
  if (id) body.id = id
  return body
}

/** 列表/导出共用的筛选 query（与分页接口一致） */
function overseasInstitutionFilterParams(
  params: Pick<
    OverseasAgencyPageParams,
    'searchKey' | 'countryName' | 'countryCode' | 'orgType' | 'status'
  >,
): Record<string, string | number> {
  const q: Record<string, string | number> = {}
  const sk = params.searchKey?.trim()
  if (sk) q.searchKey = sk
  const cn = params.countryName?.trim()
  if (cn) q.countryName = cn
  const cc = params.countryCode?.trim()
  if (cc) q.countryCode = cc
  if (params.orgType) q.orgType = params.orgType
  const st = agencyStatusToQueryParam(params.status)
  if (st !== undefined) q.status = st
  return q
}

/** POST /biz/overseas/institution/import 返回体（业务 data 内） */
export interface OverseasInstitutionImportErrorItem {
  row: number
  message: string
}

export interface OverseasInstitutionImportResult {
  totalCount: number
  successCount: number
  errorCount: number
  errorDetail: OverseasInstitutionImportErrorItem[]
  failedExcelBase64?: string
  failedExcelFileName?: string
  failedExcelError?: string
}

function unwrapApiDataObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const inner = o.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

function parseInstitutionImportResult(raw: unknown): OverseasInstitutionImportResult {
  const o = unwrapApiDataObject(raw)
  if (!o) {
    return { totalCount: 0, successCount: 0, errorCount: 0, errorDetail: [] }
  }
  const errors: OverseasInstitutionImportErrorItem[] = []
  const errRaw = o.errorDetail
  if (Array.isArray(errRaw)) {
    for (const item of errRaw) {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        const e = item as Record<string, unknown>
        errors.push({
          row: num(e.row),
          message: pickStr(e, 'message'),
        })
      }
    }
  }
  return {
    totalCount: num(o.totalCount),
    successCount: num(o.successCount),
    errorCount: num(o.errorCount),
    errorDetail: errors,
    failedExcelBase64: pickStr(o, 'failedExcelBase64') || undefined,
    failedExcelFileName: pickStr(o, 'failedExcelFileName') || undefined,
    failedExcelError: pickStr(o, 'failedExcelError') || undefined,
  }
}

/**
 * GET /biz/overseas/institution/page — 驻外机构分页列表
 */
export async function fetchOverseasAgencyPage(
  params: OverseasAgencyPageParams,
): Promise<OverseasAgencyPageResult> {
  const q: Record<string, string | number> = {
    current: params.page,
    size: params.pageSize,
    // 默认按创建时间倒序（与“驻外机构列表按创建时间降序”需求一致）
    sortField: 'createTime',
    sortOrder: 'descend',
    ...overseasInstitutionFilterParams(params),
  }

  const raw = await http.get<OverseasInstitutionPageData>('/biz/overseas/institution/page', {
    params: q,
  })
  const list: OverseasAgencyItem[] = []
  for (const row of recordsFromInstitutionPage(raw)) {
    const item = mapInstitutionRecordToItem(row)
    if (item) list.push(item)
  }
  return { list, total: totalFromInstitutionPage(raw) }
}

/**
 * GET /biz/overseas/institution/detail? id= — 出参与新增机构字段一致
 */
export async function fetchOverseasAgencyDetail(id: string): Promise<OverseasAgencyDetail | null> {
  const raw = await http.get<Record<string, unknown>>('/biz/overseas/institution/detail', {
    params: { id },
  })
  return mapInstitutionRecordToDetail(raw)
}

/**
 * POST /biz/overseas/institution/edit（body 含 id）
 */
export async function updateOverseasAgencyDetail(
  id: string,
  payload: UpdateOverseasAgencyPayload,
): Promise<void> {
  const body = buildOverseasInstitutionSaveBody(payload, id)
  await http.post<void>('/biz/overseas/institution/edit', body)
}

/**
 * POST /biz/overseas/institution/add（body 不含 id）
 */
export async function createOverseasAgency(payload: CreateOverseasAgencyPayload): Promise<void> {
  const body = buildOverseasInstitutionSaveBody(payload)
  await http.post<void>('/biz/overseas/institution/add', body)
}

/**
 * GET /biz/overseas/institution/importTemplate — 下载导入模板（Excel）
 */
export async function downloadOverseasInstitutionImportTemplate(): Promise<void> {
  await http.download('/biz/overseas/institution/importTemplate', '驻外机构批量导入模板.xlsx')
}

export async function fetchOverseasInstitutionImportTemplate(): Promise<ArrayBuffer> {
  const blob = await http.getBlob('/biz/overseas/institution/importTemplate')
  return blob.arrayBuffer()
}

function parseInstitutionImportPreviewResult(
  raw: unknown,
): import('@/types/importPreview').ImportPreviewResult {
  const o = unwrapApiDataObject(raw)
  if (!o) {
    return { totalCount: 0, importableCount: 0, errorCount: 0, rows: [] }
  }
  const rowsRaw = o.rows
  const rows: import('@/types/importPreview').ImportPreviewRow[] = []
  if (Array.isArray(rowsRaw)) {
    for (const item of rowsRaw) {
      if (!item || typeof item !== 'object') continue
      const r = item as Record<string, unknown>
      const dbFieldsRaw = r.dbFields
      const dbFields: import('@/types/importPreview').ImportDbFieldItem[] = []
      if (Array.isArray(dbFieldsRaw)) {
        for (const f of dbFieldsRaw) {
          if (!f || typeof f !== 'object') continue
          const x = f as Record<string, unknown>
          dbFields.push({
            column: pickStr(x, 'column'),
            label: pickStr(x, 'label'),
            value: pickStr(x, 'value'),
            confidential: x.confidential === true,
            inferred: x.inferred === true,
            autoGenerated: x.autoGenerated === true,
          })
        }
      }
      rows.push({
        excelRow: num(r.excelRow),
        rowLabel: pickStr(r, 'rowLabel') || '（无名称）',
        dbFields,
        warnings: Array.isArray(r.warnings) ? r.warnings.map(String) : [],
        errors: Array.isArray(r.errors) ? r.errors.map(String) : [],
        importable: r.importable === true || r.importable === 'true' || r.importable === 1,
      })
    }
  }
  return {
    totalCount: num(o.totalCount),
    importableCount: num(o.importableCount),
    errorCount: num(o.errorCount),
    icspEnabled: o.icspEnabled === true,
    confidentialHint: pickStr(o, 'confidentialHint') || undefined,
    rows,
  }
}

/** POST /biz/overseas/institution/import/preview */
export async function previewOverseasInstitutionImport(
  file: File,
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/institution/import/preview', fd, {
    timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  })
  return parseInstitutionImportPreviewResult(raw)
}

/** POST /biz/overseas/institution/import/preview/chunk */
export async function previewOverseasInstitutionChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/institution/import/preview/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parseImportPreviewResult(raw)
}

/** POST /biz/overseas/institution/import/prepare */
export async function prepareOverseasInstitutionImport(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/institution/import/prepare',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/** POST /biz/overseas/institution/import/cache/refresh */
export async function refreshOverseasInstitutionImportCache(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/institution/import/cache/refresh',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/** POST /biz/overseas/institution/import/chunk */
export async function importOverseasAgencyChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportChunkResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/institution/import/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parseImportChunkResult(raw)
}

/**
 * POST /biz/overseas/institution/import — 批量导入（multipart，字段名 file）
 */
export async function importOverseasAgencyFile(
  file: File,
): Promise<import('@/types/importPreview').ImportCommitResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/institution/import', fd)
  return parseInstitutionImportResult(raw)
}

/**
 * GET /biz/overseas/institution/export — 导出 Excel（筛选条件与列表一致）
 */
export async function exportOverseasAgencyFile(
  params: Omit<OverseasAgencyPageParams, 'page' | 'pageSize'>,
): Promise<void> {
  await http.download('/biz/overseas/institution/export', '驻外机构导出.xlsx', {
    params: {
      ...overseasInstitutionFilterParams(params),
      sortField: 'createTime',
      sortOrder: 'descend',
    },
  })
}

/**
 * POST /biz/overseas/institution/delete — 删除驻外机构
 */
export async function deleteOverseasAgency(id: string): Promise<void> {
  const v = String(id ?? '').trim()
  if (!v) return
  await http.post<void>('/biz/overseas/institution/delete', { id: v })
}
